import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, Heart, ThumbsDown, Ban, MessageSquare, Clock, Pencil, Check, X as XIcon, Clock3 } from "lucide-react";
import RestaurantTrendChart from "@/components/RestaurantTrendChart";
import { formatTime } from "@/lib/time";
import { useAuth, formatError } from "@/lib/auth";
import { toast } from "sonner";

const CUISINE_OPTIONS = ["American","Italian","Chinese","Thai","Mexican","Indian","Japanese","Korean","Vietnamese","Mediterranean","Seafood","Steakhouse","French","Spanish","Brazilian","Cafe","Vegetarian","Restaurant"];

const TAG_ICON = { favorite: Heart, disliked: ThumbsDown, never: Ban };
const TAG_TONE = { favorite: "text-rose-600", disliked: "text-amber-600", never: "text-red-600" };
const TAG_LABEL = { favorite: "marked as Favorite", disliked: "marked as Dislike", never: "marked Never visit again" };

export default function RestaurantDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [filter, setFilter] = useState("all"); // all | ratings | preferences
  const [memberFilter, setMemberFilter] = useState(""); // member_id or ""
  const [editingCuisine, setEditingCuisine] = useState(false);
  const [cuisineDraft, setCuisineDraft] = useState("");
  const canEdit = user && (user.role === "admin" || user.role === "organizer");

  useEffect(() => {
    api.get(`/restaurants/${id}`).then(r=>setData(r.data));
    api.get(`/restaurants/${id}/preference-timeline`).then(r=>setTimeline(r.data.entries || [])).catch(()=>{});
  }, [id]);

  const saveCuisine = async () => {
    try {
      const { data: updated } = await api.patch(`/restaurants/${id}`, { cuisine: cuisineDraft });
      setData(d => ({ ...d, cuisine: updated.cuisine }));
      setEditingCuisine(false);
      toast.success("Cuisine updated");
    } catch (e) { toast.error(formatError(e)); }
  };

  const members = useMemo(() => {
    const m = new Map();
    timeline.forEach(e => { if (e.member_id) m.set(e.member_id, `${e.first_name || ""} ${e.last_name || ""}`.trim()); });
    return Array.from(m.entries()).map(([id, name]) => ({ id, name })).sort((a,b) => a.name.localeCompare(b.name));
  }, [timeline]);

  const filtered = useMemo(() => timeline.filter(e => {
    if (filter === "ratings" && e.kind !== "rating") return false;
    if (filter === "preferences" && e.kind !== "preference") return false;
    if (memberFilter && e.member_id !== memberFilter) return false;
    return true;
  }), [timeline, filter, memberFilter]);

  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="restaurant-profile-card">
      <div>
        <div className="overline mb-1 flex items-center gap-2">
          {!editingCuisine && (
            <>
              <span data-testid="restaurant-cuisine-label">{data.cuisine} · {"$".repeat(data.price_level)}</span>
              {canEdit && (
                <button onClick={()=>{setCuisineDraft(data.cuisine || ""); setEditingCuisine(true);}}
                        data-testid="edit-cuisine-btn"
                        className="text-primary hover:underline text-[10px] flex items-center gap-1">
                  <Pencil className="w-3 h-3"/>edit
                </button>
              )}
            </>
          )}
          {editingCuisine && (
            <span className="flex items-center gap-2">
              <select value={cuisineDraft} onChange={e=>setCuisineDraft(e.target.value)}
                      className="border rounded-md h-8 px-2 bg-background text-xs"
                      data-testid="cuisine-select">
                {CUISINE_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={saveCuisine} className="text-primary" data-testid="save-cuisine-btn"><Check className="w-4 h-4"/></button>
              <button onClick={()=>setEditingCuisine(false)} className="text-muted-foreground" data-testid="cancel-cuisine-btn"><XIcon className="w-4 h-4"/></button>
            </span>
          )}
        </div>
        <h1 className="font-serif text-4xl">{data.name}</h1>
        <div className="text-sm text-muted-foreground mt-1 mono">{data.address}, {data.city}, {data.state} {data.zip_code}</div>
        {data.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-3">{data.tags.map(t => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary">{t.replace("_"," ")}</span>)}</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="overline mb-1">Group rating</div>
          <div className="font-serif text-3xl flex items-center gap-2" data-testid="restaurant-rating-stars">
            {data.avg_rating ?? "—"} {data.avg_rating && <Star className="w-6 h-6 fill-amber-400 text-amber-400"/>}
          </div>
          <div className="text-xs text-muted-foreground mt-1">{data.num_ratings} ratings</div>
        </Card>
        <Card className="p-5">
          <div className="overline mb-1">Visits</div>
          <div className="font-serif text-3xl">{data.num_visits}</div>
          <div className="text-xs text-muted-foreground mt-1">Last: {data.last_visit || "never"}</div>
        </Card>
        <Card className="p-5">
          <div className="overline mb-1">Reservation</div>
          <div className="text-sm mt-1">{data.reservation_required ? "Required" : "Walk-in OK"}</div>
          <div className="text-xs text-muted-foreground mt-1 mono">{data.phone}</div>
          {data.website && <a className="text-xs text-primary" href={data.website} target="_blank" rel="noreferrer">Website</a>}
        </Card>
      </div>

      {data.hours && (
        <Card className="p-5" data-testid="restaurant-hours">
          <div className="overline mb-2 flex items-center gap-1"><Clock3 className="w-3 h-3"/>Hours</div>
          <pre className="text-xs mono whitespace-pre-wrap text-muted-foreground leading-5">{data.hours}</pre>
        </Card>
      )}

      <div>
        <h2 className="font-serif text-2xl mb-3">Visit history</h2>
        {data.visits.length === 0 && <div className="text-muted-foreground text-sm">No visits recorded yet.</div>}
        <div className="space-y-2">
          {data.visits.map(v=>(
            <Card key={v.id} data-testid="restaurant-past-visit-row" className="p-4 flex justify-between">
              <div className="text-sm">{v.dinner_date} · {formatTime(v.approved_time || v.recommended_time)}</div>
              <div className="text-xs text-muted-foreground">{v.participant_member_ids?.length} guests · {v.status.replaceAll("_"," ")}</div>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Clock className="w-5 h-5 text-primary"/>
          <h2 className="font-serif text-2xl">Timeline · who liked it and when</h2>
        </div>

        {timeline.length > 0 && <RestaurantTrendChart entries={timeline}/>}

        {timeline.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap mb-4 mt-4" data-testid="timeline-filter-bar">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList>
                <TabsTrigger value="all" data-testid="timeline-tab-all">All</TabsTrigger>
                <TabsTrigger value="ratings" data-testid="timeline-tab-ratings">Ratings only</TabsTrigger>
                <TabsTrigger value="preferences" data-testid="timeline-tab-preferences">Preferences only</TabsTrigger>
              </TabsList>
            </Tabs>
            <select value={memberFilter} onChange={e=>setMemberFilter(e.target.value)}
                    className="border rounded-md h-9 px-2 bg-background text-sm" data-testid="timeline-member-filter">
              <option value="">All members</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name || m.id}</option>)}
            </select>
            <span className="text-xs text-muted-foreground mono">{filtered.length} of {timeline.length} entries</span>
          </div>
        )}

        {timeline.length === 0 && <div className="text-muted-foreground text-sm">No preference snapshots or ratings yet. Once dinners are planned or rated, everyone's changes appear here.</div>}
        {timeline.length > 0 && filtered.length === 0 && <div className="text-muted-foreground text-sm">No entries match the current filter.</div>}
        {filtered.length > 0 && (
          <div className="relative pl-6 space-y-4" data-testid="restaurant-preference-timeline">
            <div className="absolute left-2 top-2 bottom-2 w-px bg-border"/>
            {filtered.map((e) => {
              const day = (e.at || "").slice(0,10);
              const rowKey = `${e.at || ""}-${e.kind}-${e.member_id || ""}-${e.tag || e.stars || ""}`;
              if (e.kind === "rating") {
                return (
                  <div key={rowKey} className="relative" data-testid="timeline-rating-entry">
                    <div className="absolute -left-4 top-2 w-3 h-3 rounded-full bg-amber-400 border-2 border-background"/>
                    <div className="text-xs mono text-muted-foreground mb-1">{day} {e.dinner_date && <span>· dinner {e.dinner_date}</span>}</div>
                    <div className="text-sm">
                      <strong>{e.first_name} {e.last_name}</strong>{" "}
                      rated {" "}
                      <span className="inline-flex align-middle">{Array.from({length: e.stars}).map((_, j) => <Star key={j} className="w-3 h-3 fill-amber-400 text-amber-400"/>)}</span>
                    </div>
                    {e.comment && <div className="text-xs text-muted-foreground mt-1 flex gap-1 items-start"><MessageSquare className="w-3 h-3 mt-0.5 shrink-0"/>"{e.comment}"</div>}
                  </div>
                );
              }
              const Icon = TAG_ICON[e.tag] || Heart;
              const tone = TAG_TONE[e.tag] || "";
              return (
                <div key={rowKey} className="relative" data-testid="timeline-preference-entry">
                  <div className={`absolute -left-4 top-2 w-3 h-3 rounded-full border-2 border-background ${e.tag==="never"?"bg-red-500":e.tag==="disliked"?"bg-amber-500":"bg-rose-500"}`}/>
                  <div className="text-xs mono text-muted-foreground mb-1">{day} {e.dinner_date && <span>· recommendation for {e.dinner_date}</span>}</div>
                  <div className="text-sm flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${tone}`}/>
                    <span><strong>{e.first_name} {e.last_name}</strong> {TAG_LABEL[e.tag] || e.tag}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
